/* A Prayer Request card.
 *
 * Follows the client's wireframe (docs/CLIENT_TERMINOLOGY.md):
 *   - a colour-coded bar across the header: Red, Yellow, Green
 *   - a Responses count
 *   - four response actions, in this order and wording:
 *     Comment · I Will Pray · I am Praying · Prayed
 */
import { useState } from 'react'
import {
  Check,
  Globe,
  HandHeart,
  MessageCircle,
  Pencil,
  Settings2,
  Share2,
  Star,
  Users,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
// The same thread the feed uses. Prayer comments differ only in carrying a
// help_kind badge, which is a prop.
import { CommentThread as Thread } from '@/features/comments/CommentThread'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Modal } from '@/components/ui/modal'
import { MediaGrid } from '@/features/feed/MediaGrid'
import { cn, compactCount, timeAgo } from '@/lib/utils'
import { EditDialog } from '@/features/feed/EditDialog'
import { ComposerModal } from '@/features/feed/Composer'
import {
  useAddPrayerComment,
  useEditPrayerRequest,
  useHelpOptions,
  usePrayerComments,
  useRespondToPrayer,
  useSetPrayerState,
  useSetRequestFollowing,
} from '@/api/queries'
import type { PrayerRequest, PrayerState, ResponseType } from '@/types/api'

/* The client's states, with the meaning they gave each colour.
   `short` is the chip in the prototype — RED / YELLOW / GREEN — and `label`
   the full meaning, used where there is room for it. */
const STATES: Record<
  PrayerState,
  { label: string; short: string; bar: string; chip: string }
> = {
  red: {
    label: 'Critical / Urgent',
    short: 'Urgent',
    bar: 'bg-pr-red',
    chip: 'bg-pr-red-soft text-pr-red',
  },
  yellow: {
    label: 'Continuous / Long term',
    short: 'Long term',
    bar: 'bg-pr-yellow',
    chip: 'bg-pr-yellow-soft text-pr-yellow-ink',
  },
  green: {
    label: 'Answered',
    short: 'Answered',
    bar: 'bg-pr-green',
    chip: 'bg-pr-green-soft text-pr-green-ink',
  },
}

/* The three SELECTIONS, in the client's order and wording.
 *
 * Comment is deliberately NOT in this list. The client's own text:
 *
 *   Comment : He can comment on the PR
 *   He can select "I Will Pray"
 *   He can select "I am Praying"
 *   He can select "Prayed"
 *
 * Comment takes TEXT; these three are one-tap. Built as a fourth identical
 * toggle — which this file used to do — there is no way to actually comment on
 * a Prayer Request.
 *
 * The three are mutually exclusive with each other. A comment is not: someone
 * can be praying AND have said something.
 */
const SELECTIONS: { type: ResponseType; label: string }[] = [
  { type: 'will_pray', label: 'I Will Pray' },
  { type: 'praying', label: 'I am Praying' },
  { type: 'prayed', label: 'Prayed' },
]

export function PrayerCard({
  request,
  viewerId,
  focusComment,
  highlighted = false,
}: {
  request: PrayerRequest
  viewerId?: string
  /** Arriving from a "replied to you" tap — open the thread on that comment. */
  focusComment?: string | null
  /** Ringed, because a notification sent the reader to this one. */
  highlighted?: boolean
}) {
  // Open already, when the reader was sent here to read one particular reply.
  // Making them tap Comment first would put the thing they came for one step
  // further away than it was before.
  const [commenting, setCommenting] = useState(Boolean(focusComment))
  const [offering, setOffering] = useState(false)
  const [editing, setEditing] = useState(false)
  const [sharingTestimony, setSharingTestimony] = useState(false)
  const setRequestFollowing = useSetRequestFollowing()
  const setState = useSetPrayerState()
  const editRequest = useEditPrayerRequest()

  const state = STATES[request.state]
  const isAuthor = viewerId === request.author.id

  return (
    <Card
      className={cn(
        'animate-fade-in overflow-hidden',
        highlighted && 'ring-2 ring-brand',
      )}
    >
      {/* The colour-coded bar from the wireframe. */}
      <div className={cn('h-1.5 w-full', state.bar)} aria-hidden />

      <div className="flex items-start gap-s3 p-s4 pb-s3">
        <Avatar name={request.author.name} src={request.author.avatar_url} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-md font-semibold leading-tight">
            {request.author.name}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-ink-2">
            <span>{timeAgo(request.created_at)}</span>
            {/* Beside the time, not instead of it — people commit to praying
                for what they read, and when it was asked still matters. */}
            {request.edited_at ? (
              <span className="text-ink-3" title="This Prayer Request was edited">
                · edited
              </span>
            ) : null}
            <span aria-hidden>·</span>
            {/* From your church, or for everyone — the same marker a post
                carries, so a church's own requests stand out from the rest. */}
            {request.visibility === 'church' ? (
              <span
                className="flex min-w-0 items-center gap-1"
                title={`Only ${request.church_name ?? 'your church'} can see this`}
              >
                <Users className="h-3 w-3 shrink-0" aria-hidden />
                <span className="max-w-[12rem] truncate">
                  {request.church_name ?? 'Your church'}
                </span>
              </span>
            ) : (
              <Globe className="h-3 w-3" aria-label="Everyone" />
            )}
          </div>
        </div>

        {/* Colour AND meaning. The dot carries the client's Red/Yellow/Green
            code; the text says what it means, because "YELLOW" names the paint
            rather than the situation. */}
        <span
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-pill px-s2 py-0.5 text-xs font-bold',
            state.chip,
          )}
          title={state.label}
        >
          <span
            className={cn('h-2 w-2 shrink-0 rounded-pill', state.bar)}
            aria-hidden
          />
          {state.short}
        </span>

        {/* 8 — Share My Testimony. Green state AND author only: "this option
            will be enabled only for the owner of the PR". Opens the composer
            in testimony mode; the post goes to the feed linked back to this
            request, and everyone who prayed on it is told. */}
        {request.state === 'green' && isAuthor ? (
          <button
            onClick={() => setSharingTestimony(true)}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-pr-green transition-colors hover:bg-pr-green-soft"
            title="Share My Testimony"
            aria-label="Share My Testimony"
          >
            <Share2 className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        {sharingTestimony ? (
          <ComposerModal
            testimonyOf={request.id}
            onClose={() => setSharingTestimony(false)}
          />
        ) : null}

        {/* Follow THIS request — "tell me about this one". Distinct from the
            page switch in the header, which is "send me everything". Not
            offered to the author: you are already told about your own. */}
        {!isAuthor ? (
          <button
            onClick={() =>
              setRequestFollowing.mutate({
                id: request.id,
                following: !request.following,
              })
            }
            aria-pressed={request.following}
            aria-label={
              request.following
                ? 'Following this Prayer Request'
                : 'Follow this Prayer Request'
            }
            title={
              request.following
                ? 'You will hear about this Prayer Request'
                : 'Follow this Prayer Request'
            }
            className={cn(
              'grid h-8 w-8 shrink-0 place-items-center rounded-pill transition-colors',
              request.following
                ? 'text-brand hover:bg-brand-soft'
                : 'text-ink-2 hover:bg-hover-2',
            )}
          >
            <Star
              className={cn('h-4 w-4', request.following && 'fill-brand')}
              aria-hidden
            />
          </button>
        ) : null}

        {/* Only the author may change the state — the client's rule, and the
            right one: nobody else marks your request answered. The gear sits
            beside the chip, as it does in the wireframe. */}
        {isAuthor ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2"
                aria-label="Change state"
              >
                <Settings2 className="h-4 w-4" aria-hidden />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={4}
                className="z-50 min-w-[13rem] rounded-md bg-surface p-s1 shadow-3"
              >
                {(Object.keys(STATES) as PrayerState[]).map((key) => (
                  <DropdownMenu.Item
                    key={key}
                    onSelect={() => setState.mutate({ id: request.id, state: key })}
                    className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                  >
                    <span
                      className={cn('h-3 w-3 rounded-pill', STATES[key].bar)}
                      aria-hidden
                    />
                    {STATES[key].label}
                    {key === request.state ? (
                      <Check className="ml-auto h-4 w-4 text-brand" aria-hidden />
                    ) : null}
                  </DropdownMenu.Item>
                ))}

                {/* Words only, below the states and behind a rule: the state
                    is what this menu is mostly for, and the two are different
                    kinds of change. */}
                <DropdownMenu.Separator className="my-s1 h-px bg-line-2" />
                <DropdownMenu.Item
                  onSelect={() => setEditing(true)}
                  className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                  Edit request
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : null}
      </div>

      {request.body ? (
        <p className="whitespace-pre-wrap break-words px-s4 pb-s3 text-md leading-normal">
          {request.body}
        </p>
      ) : null}

      <MediaGrid media={request.media ?? []} />

      {request.response_count > 0 ? (
        <div className="px-s4 pb-s2 text-sm text-ink-2">
          {compactCount(request.response_count)}{' '}
          {request.response_count === 1 ? 'response' : 'responses'}
        </div>
      ) : null}



      {/* The client's four options, side by side and one tap each:
          Comment · I Will Pray · I am Praying · Prayed. Two to a row on a
          narrow screen, so "I am Praying" is never cut short. */}
      <div className="mx-s4 grid grid-cols-2 gap-s2 border-t border-line-2 py-s2 sm:grid-cols-4">
        <button
          onClick={() => setCommenting((v) => !v)}
          aria-expanded={commenting}
          className={cn(
            'flex min-h-[38px] items-center justify-center gap-1.5 rounded px-s2 text-sm font-semibold transition-colors',
            commenting ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-hover',
          )}
        >
          <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
          Comment
          {request.comment_count ? (
            <span className="font-bold">{request.comment_count}</span>
          ) : null}
        </button>

        <ResponseButtons request={request} />
      </div>

      {/* Card-first: you are already reading the need, so the offer is
          grounded in it. A global list of every request needing help does
          not scale humanly — an endless list of strangers' needs is
          paralysing, and nobody gets helped. */}
      {!isAuthor ? (
        <div className="mx-s4 border-t border-line-2 py-s2">
          <button
            onClick={() => setOffering(true)}
            className="flex min-h-[38px] w-full items-center justify-center gap-1.5 rounded px-s3 text-sm font-semibold text-ink-2 transition-colors hover:bg-hover"
          >
            <HandHeart className="h-4 w-4" aria-hidden />
            Offer help
          </button>
        </div>
      ) : null}

      {offering ? (
        <OfferHelpDialog
          request={request}
          onClose={() => setOffering(false)}
        />
      ) : null}

      {/* Words only — no photos and no feeling, because the API takes neither
          on a Prayer Request, and offering a control the server would refuse
          reads as the app being broken. */}
      {editing ? (
        <EditDialog
          title="Edit Prayer Request"
          initialBody={request.body}
          placeholder="What would you like prayer for?"
          saving={editRequest.isPending}
          error={
            editRequest.isError
              ? ((editRequest.error as Error)?.message ?? 'That did not save.')
              : null
          }
          onClose={() => setEditing(false)}
          onSave={(edit) =>
            editRequest.mutate(
              { id: request.id, body: edit.body },
              { onSuccess: () => setEditing(false) },
            )
          }
        />
      ) : null}

      {commenting ? (
        <PrayerCommentBox request={request} focusComment={focusComment} />
      ) : null}

      {/* 7 — "Promise for you :", below the actions as the wireframe places
          it. Absent for the first five minutes: the client asked for the
          promise to be "attached after 5 mins", so a brand new request simply
          has no block rather than a placeholder saying one is coming. */}
      {request.promise ? (
        <div className="m-s4 mt-0 rounded border border-pr-green/30 bg-pr-green-soft px-s4 py-s3">
          <div className="text-sm font-bold text-pr-green">Promise for you :</div>
          <p className="mt-1 italic leading-normal text-pr-green-ink">
            “{request.promise.verse}”
          </p>
          <div className="mt-1.5 text-sm font-bold text-pr-green">
            {request.promise.reference}
          </div>
        </div>
      ) : null}

    </Card>
  )
}

/** The comment thread.
 *
 *  "Comment : He can comment on the PR" — it takes text. The three selections
 *  beside it do not, which is why this is a separate control rather than a
 *  fourth button that behaves like the others.
 *
 *  Comments have their own table, so someone can be praying AND have said
 *  something, and can say several things — see migration 0007.
 */
function PrayerCommentBox({
  request,
  focusComment,
}: {
  request: PrayerRequest
  focusComment?: string | null
}) {
  const comments = usePrayerComments(request.id)
  const addComment = useAddPrayerComment(request.id)
  const options = useHelpOptions()

  const items = comments.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <Thread
      comments={items}
      isLoading={comments.isLoading}
      hasMore={comments.hasNextPage}
      onLoadMore={() => comments.fetchNextPage()}
      isLoadingMore={comments.isFetchingNextPage}
      isSending={addComment.isPending}
      onSend={(body, parentId) =>
        addComment.mutate({ body, parent_id: parentId })
      }
      placeholder="Say something…"
      focusId={focusComment}
      // The one thing a prayer comment has that a post comment does not: an
      // offer of help reads as an offer rather than an ordinary remark.
      renderBadge={(c) =>
        c.help_kind ? (
          <span className="inline-flex items-center gap-1 rounded-pill bg-success px-s2 py-0.5 text-xs font-semibold text-white">
            <HandHeart className="h-3 w-3" aria-hidden />
            {options.data?.find((o) => o.kind === c.help_kind)?.label ??
              'Offer of help'}
          </span>
        ) : null
      }
    />
  )
}

/** The three selections, as three buttons.
 *
 *  The client lists them as separate options beside Comment, so each is its
 *  own button with its own count, and the one you chose is lit.
 *
 *  Choosing is ONE TAP and silent. The client's doc ties these to the
 *  Responses count — "saying I am praying then this count must increase" — so
 *  they are counted commitments, closer to a reaction than to a comment.
 *  Tapping the lit one clears it, the same gesture both ways. Anybody who
 *  wants to say something has Comment beside them.
 */
function ResponseButtons({ request }: { request: PrayerRequest }) {
  const respond = useRespondToPrayer()
  const current = request.viewer_response

  return (
    <>
      {SELECTIONS.map(({ type, label }) => {
        const chosen = current === type
        const count = request.counts?.[type] ?? 0
        return (
          <button
            key={type}
            onClick={() =>
              respond.mutate({ id: request.id, response_type: chosen ? null : type })
            }
            aria-pressed={chosen}
            className={cn(
              'flex min-h-[38px] items-center justify-center gap-1.5 rounded px-s2 text-sm font-semibold transition-colors',
              chosen ? 'bg-brand-soft text-brand' : 'text-ink-2 hover:bg-hover',
            )}
          >
            {chosen ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
            <span className="whitespace-nowrap">{label}</span>
            {count ? <span className="font-bold">{compactCount(count)}</span> : null}
          </button>
        )
      })}
    </>
  )
}

/** Offer help on THIS request.
 *
 *  Card-first, because a global list of every request needing help does not
 *  scale: at a million requests it is an endless wall of strangers' needs, and
 *  the reader helps nobody. Starting from the request you are already reading
 *  makes the offer concrete.
 *
 *  The author's own tag is preselected — they said what would meet the need,
 *  so that is the sensible default.
 */
/** Offering help on ONE request.
 *
 *  Exported because the Help dialog's match list needs the same thing, and had
 *  its own cut-down version: a bare textarea that posted the offer with
 *  whatever kind the AUTHOR had tagged — so on an untagged request, which is
 *  most of them, the offer carried no kind at all. One dialog, one behaviour.
 */
export function OfferHelpDialog({
  request,
  onClose,
}: {
  request: PrayerRequest
  onClose: () => void
}) {
  const [kind, setKind] = useState<string | null>(request.help_kind)
  const [note, setNote] = useState('')
  const options = useHelpOptions()
  const addComment = useAddPrayerComment(request.id)

  function send() {
    if (!kind || addComment.isPending) return
    // The chosen option IS the offer. A note only adds detail — when and how
    // to reach you — so an empty one falls back to the option's own wording
    // rather than blocking the offer.
    const label =
      options.data?.find((o) => o.kind === kind)?.label ?? 'I can help'
    addComment.mutate(
      { body: note.trim() || label, help_kind: kind },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title="Offer help"
      width="480px"
      guardClose={Boolean(note.trim())}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={send}
          disabled={!kind}
          loading={addComment.isPending}
        >
          Post offer
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <p className="text-base text-ink-2">
          {request.author.name} will see your offer on this Prayer Request and
          can reply there.
        </p>

        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-ink-2">
            What can you offer?
          </span>
          <select
            value={kind ?? ''}
            onChange={(e) => setKind(e.target.value || null)}
            className="h-11 w-full rounded border border-line bg-surface px-s3 text-base outline-none focus:border-brand focus:shadow-focus"
          >
            <option value="">Choose…</option>
            {options.data?.map((o) => (
              <option key={o.kind} value={o.kind}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-ink-2">
            Anything to add? <span className="font-normal">(optional)</span>
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            placeholder="When you are free, how to reach you…"
            aria-label="Your offer"
            rows={3}
            className="w-full resize-none rounded border border-line bg-surface p-s3 text-base outline-none focus:border-brand focus:shadow-focus"
          />
        </label>
      </div>
    </Modal>
  )
}
